/**
 * Menu packs: the menu-board designs as a file the server publishes and players download.
 *
 * Designs are code (layout, pagination, CSS), and a player used to know only the designs it was
 * built with, so every new design meant a new APK and installer. Instead the API bundles this
 * module's renderer into one script, lists it in each manifest by its sha256, and players fetch
 * it like any other cached file, verify it, and draw menus with it. Players fall back to the
 * renderer built into them whenever there is no pack, it is not downloaded yet, or it fails to
 * load, so a screen is never left without its board.
 *
 * Fonts are not in the pack (they are embedded in the players), so a design that needs a new
 * font family still needs a player release.
 */
import { MENU_CSS, MENU_TEMPLATES, menuMediaIds, paginateMenu, renderMenuPage } from './index.js';

/**
 * The contract between a pack and the players that run it. Bump it only for a change an
 * existing player cannot handle; players ignore packs with any other value.
 */
export const MENU_PACK_API = 1;

/** The global the bundled script assigns its pack to. */
export const MENU_PACK_GLOBAL = '__signageMenuPack';

/** What a pack provides. Players use only these. */
export interface MenuPack {
  api: number;
  renderMenuPage: typeof renderMenuPage;
  paginateMenu: typeof paginateMenu;
  menuMediaIds: typeof menuMediaIds;
  MENU_CSS: string;
  templateIds: string[];
}

let builtIn: MenuPack | null = null;

/**
 * The renderer this build carries, as a pack: what players use when no other is loaded. A
 * function rather than a constant because this module and the menu index import each other.
 */
export function builtInMenuPack(): MenuPack {
  builtIn ??= {
    api: MENU_PACK_API,
    renderMenuPage,
    paginateMenu,
    menuMediaIds,
    MENU_CSS,
    templateIds: MENU_TEMPLATES.map((t) => t.id),
  };
  return builtIn;
}

/**
 * Runs a downloaded pack script and returns its pack. Throws when the script fails or does not
 * provide a pack this player understands, so the caller can keep the built-in renderer.
 *
 * Only call it with a file whose sha256 matched the manifest: the manifest comes from our API
 * over an authenticated connection, which is what makes running the script safe.
 */
export function loadMenuPack(source: string): MenuPack {
  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  const pack = new Function(`${source}\n;return typeof ${MENU_PACK_GLOBAL} === 'undefined' ? null : ${MENU_PACK_GLOBAL};`)() as
    | Partial<MenuPack>
    | null;
  if (!pack || pack.api !== MENU_PACK_API) throw new Error(`Menu pack API ${pack?.api ?? 'missing'} is not ${MENU_PACK_API}`);
  for (const fn of ['renderMenuPage', 'paginateMenu', 'menuMediaIds'] as const) {
    if (typeof pack[fn] !== 'function') throw new Error(`Menu pack has no ${fn}`);
  }
  if (typeof pack.MENU_CSS !== 'string') throw new Error('Menu pack has no CSS');
  return { ...(pack as MenuPack), templateIds: Array.isArray(pack.templateIds) ? pack.templateIds : [] };
}
